import { Injectable, computed, inject } from '@angular/core'

import { BreadcrumbItem } from '@hyperkit'

import { NavigationService } from '../navigation.service'

import { HuntViewService } from './hunt-view.service'

/**
 * BreadcrumbService builds the breadcrumb trails
 * for all Hunt‐related routes.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly nav = inject(NavigationService)
  private readonly view = inject(HuntViewService)

  /**
   * `/hunt/hunts`
   * — the top‐level “Hunts” list.
   */
  readonly huntList = computed<BreadcrumbItem[]>(() => [
    {
      text: 'Hunts',
      link: '/hunt/hunts',
      selected: true,
      extras: {
        // preserve default list state
        queryParams: {
          state:
            '(archived:!f,filter:!n,skip:0,sort:!((dir:desc,field:created)),take:10)',
        },
      },
    },
  ])

  /**
   * `/hunt/:id/workbench`
   * — the workbench for a single Hunt.
   */
  readonly workbench = computed<BreadcrumbItem[]>(() => {
    const id = this.nav.huntId() // signal<string|null>
    const name = this.view.name() // signal<string>
    return id
      ? [
          { text: 'Hunts', link: '/hunt/hunts', selected: false },
          {
            text: name,
            link: `/hunt/${id}/workbench`,
            selected: true,
          },
        ]
      : []
  })

  /**
   * `/hunt/:id/edit-report/...`
   * — the multi‐step edit-report flow.
   */
  readonly editReport = computed<BreadcrumbItem[]>(() => {
    const id = this.nav.huntId()
    const section = this.nav.selectedEditReportSection()
    const base = `/hunt/${id}/edit-report`
    const title =
      section === 'overview'
        ? 'Overview'
        : section === 'conclusion'
          ? 'Conclusion'
          : 'Technical Summary'

    return id
      ? [
          { text: 'Hunts', link: '/hunt/hunts', selected: false },
          {
            text: this.view.name(),
            link: base,
            selected: false,
          },
          {
            text: title,
            link:
              section === 'overview'
                ? base
                : section === 'conclusion'
                  ? `${base}/conclusion`
                  : `${base}/technical-summary/${section}`,
            selected: true,
          },
        ]
      : []
  })

  /**
   * `/hunt/:id/final-report/...`
   * — the multi‐step final-report flow.
   */
  readonly finalReport = computed<BreadcrumbItem[]>(() => {
    const id = this.nav.huntId()
    const section = this.nav.selectedFinalReportSection()
    const base = `/hunt/${id}/final-report`
    let title: string

    if (section === 'overview') title = 'Overview'
    else if (section === 'coverage') title = 'Coverage'
    else if (section === 'conclusion') title = 'Conclusion'
    else title = 'Technical Summary'

    return id
      ? [
          { text: 'Hunts', link: '/hunt/hunts', selected: false },
          {
            text: this.view.name(),
            link: base,
            selected: false,
          },
          {
            text: title,
            link:
              section === 'overview'
                ? base
                : section === 'coverage'
                  ? `${base}/coverage`
                  : section === 'conclusion'
                    ? `${base}/conclusion`
                    : `${base}/technical-summary/${section}`,
            selected: true,
          },
        ]
      : []
  })

  /**
   * Steps in the capture-data → pivot-select dialog.
   */
  readonly capturePivotSteps = computed<
    (BreadcrumbItem & { step: 'capture' | 'pivot' })[]
  >(() => {
    const step = this.nav.pivotStep() // 'capture' | 'pivot'
    const chooseYourOwn = this.nav.chooseYourOwn() // 'true' | 'false'
    const steps: (BreadcrumbItem & { step: 'capture' | 'pivot' })[] = [
      {
        text: 'Specify Your Capture Data',
        selected: step === 'capture',
        step: 'capture',
      },
      {
        text: 'Choose Your Pivot',
        selected: step === 'pivot',
        step: 'pivot',
      },
    ]

    return chooseYourOwn ? [...steps].reverse() : steps
  })

  /**
   * Helper for environment → tool breadcrumbs.
   * @param envName  display name of the environment
   * @param toolName display name of the tool
   */
  buildToolBreadcrumbs(
    envName: string,
    toolName: string,
  ): (BreadcrumbItem & { step: 'env' | 'tool' })[] {
    return [
      { text: envName, selected: false, step: 'env' },
      { text: toolName, selected: true, step: 'tool' },
    ]
  }
}
